
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useEffect, useState } from 'react';
import shuffle from 'lodash/shuffle';
import SharedLibs from 'wizarding-world-web-shared';
import { ADD_USER_INTERESTS, INTERESTS_PICKER_SHOWN_AT, UPDATE_PROFILE } from '@utils/graphQueries';
import { trackEvent, trackClickEvent } from '@utils/analytics';
import StyledHeading from '@components/StyledHeading';
import { USER_INTERESTS_MODAL_STATE } from '@/src/constants';
import Modal from '@components/Modal';
import Button from '@components/Button';
import Image from '@components/Image';
import { getGlobalState, setSelectedUserInterests } from '@states/ProfileState';
import { useUserContext } from '@contexts/user-context';
import useShowUserInterestsModals from './hooks/useShowUserInterestsModals';
import { ReactComponent as NewsletterOptInOwl } from '../img/newsletter-optin-owl.svg';
import s from './UserInterests.module.scss';
const pageName = 'Profile';
const userInterestsModalLocation = 'User Interests Modal';
const userInterestsOptInModalLocation = 'User Interests Opt In Modal';
const UserInterests = () => {
    const { updateProfile, profile: { marketingOptInWW, loadingProfile, underage, interestsPickerShownAt }, } = useUserContext();
    const { api: wizardingWorldAPI } = SharedLibs.services;
    const selectedUserInterests = getGlobalState('selectedUserInterests');
    const userInterestsData = getGlobalState('userInterestsData');
    const [shuffledInterests, setShuffledInterests] = useState(null);
    const [draftUserInterests, setDraftUserInterests] = useState(selectedUserInterests || []);
    const [modalState, setModalState] = useState(USER_INTERESTS_MODAL_STATE.NONE);
    const [interestsPicked, setInterestsPicked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    useShowUserInterestsModals(selectedUserInterests, interestsPickerShownAt, setModalState, marketingOptInWW);
    useEffect(() => {
        if (modalState === USER_INTERESTS_MODAL_STATE.INTERESTS && wizardingWorldAPI) {
            setShuffledInterests(shuffle(userInterestsData));
            trackEvent('User Interests Modal Opened', {
                location: pageName
            });
            wizardingWorldAPI().mutate({
                mutation: INTERESTS_PICKER_SHOWN_AT
            });
            updateProfile();
        }
        else {
            setShuffledInterests(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalState, userInterestsData]);
    useEffect(() => {
        if (modalState === USER_INTERESTS_MODAL_STATE.OPT_IN) {
            trackEvent('User Interests Opt In Modal Opened', {
                location: pageName
            });
        }
    }, [modalState]);
    const saveUserInterestSelections = async () => {
        setIsSaving(true);
        const interestsToSave = draftUserInterests.map((i) => ({
            interestContentfulId: i.contentfulId,
            interest: i.categoryTitle
        }));
        await wizardingWorldAPI().mutate({
            mutation: ADD_USER_INTERESTS,
            variables: {
                userInterests: interestsToSave
            }
        });
        trackClickEvent({
            destination_url: window.location.pathname,
            label: 'Save Interests',
            location: userInterestsModalLocation,
            selected_interests: interestsToSave
        });
        setSelectedUserInterests(draftUserInterests);
        setInterestsPicked(true);
        if (!marketingOptInWW) {
            setModalState(USER_INTERESTS_MODAL_STATE.OPT_IN);
        }
        else {
            setModalState(USER_INTERESTS_MODAL_STATE.NONE);
        }
        setIsSaving(false);
    };
    const handleUserInterestClick = (index) => {
        const isSelected = draftUserInterests.includes(index);
        if (isSelected) {
            setDraftUserInterests(draftUserInterests.filter((j) => j !== index));
        }
        else {
            setDraftUserInterests([...draftUserInterests, index]);
        }
    };
    const handleOptInAccept = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        trackClickEvent({
            destination_url: window.location.pathname,
            label: 'Yes, Count Me In',
            location: userInterestsOptInModalLocation
        });
        await wizardingWorldAPI().mutate({
            mutation: UPDATE_PROFILE,
            variables: {
                marketingOptInWW: true
            }
        });
        await updateProfile();
        trackEvent('Marketing Opt In Toggled', {
            label: 'Wizarding World Digital',
            location: userInterestsOptInModalLocation,
            platform: 'Web',
            value: true,
            ww_marketing_opt_in: true
        });
        setIsSaving(false);
        setModalState(USER_INTERESTS_MODAL_STATE.NONE);
    };
    if (loadingProfile || underage || !userInterestsData || userInterestsData.length < 1)
        return null;
    return (<>
      <div className={s.root}>
        <div className={s.heading}>Your Favourite Topics</div>
        <div className={s.interests}>
          {selectedUserInterests && selectedUserInterests.length > 0 ? (selectedUserInterests.map((i, index) => {
            return (<React.Fragment key={i.contentfulId}>
                  {i.categoryTitle}
                  {index < selectedUserInterests.length - 1 && ', '}
                </React.Fragment>);
        })) : (<span>You haven&apos;t picked your favourite topics yet.</span>)}
        </div>
        <div className={s.editArea}>
          <Button type="text" label="Edit Interests" data-testid="edit-interests-button" onClick={() => {
            trackClickEvent({
                destination_url: window.location.pathname,
                label: 'Edit Interests',
                location: pageName
            });
            setModalState(USER_INTERESTS_MODAL_STATE.INTERESTS);
        }}/>
        </div>
        <Modal isOpen={modalState === USER_INTERESTS_MODAL_STATE.INTERESTS && !!shuffledInterests}>
          <div className={s.userInterestModalContainer} data-testid="user-interests-modal">
            <div className={s.modalHeading}>
              <StyledHeading text="Follow your favourite topics"/>
            </div>
            <div className={s.modalSubHeading}/>
            <div className={s.interestSelection}>
              <ul className={s.selectionList} data-testid="user-interests-selection-list">
                {shuffledInterests &&
            shuffledInterests.length > 0 &&
            shuffledInterests.map((userInterest) => {
                const isSelected = draftUserInterests.some((j) => j.contentfulId === userInterest.contentfulId);
                return (<li key={userInterest.contentfulId}>
                        <div className={s.interestContainer} onClick={() => {
                        handleUserInterestClick(userInterest);
                    }} onKeyDown={(e) => {
                        /* istanbul ignore else */
                        if (e.keyCode === 13) {
                            handleUserInterestClick(userInterest);
                        }
                    }} tabIndex="0" role="button" aria-label="Previous">
                          <div className={[
                        s.interestImageContainer,
                        isSelected ? s.selected : '',
                    ].join(' ')}>
                            {userInterest.categoryImage && (<Image imageSet={[{ src: userInterest.categoryImage?.image?.file?.url }]} contain center alt={userInterest.categoryTitle}/>)}
                          </div>
                          <div className={s.interestName}>{userInterest.categoryTitle}</div>
                        </div>
                      </li>);
            })}
              </ul>
            </div>
            <div className={s.ctaContainer}>
              <Button type="secondary" label="Not Now" data-testid="user-interests-cancel-button" disabled={isSaving} onClick={() => {
            trackClickEvent({
                destination_url: window.location.pathname,
                label: 'Not Now',
                location: userInterestsModalLocation
            });
            setDraftUserInterests(selectedUserInterests);
            setModalState(USER_INTERESTS_MODAL_STATE.NONE);
        }}/>
              <Button label="Save Interests" data-testid="user-interests-save-button" disabled={draftUserInterests.length < 1} loading={isSaving} onClick={saveUserInterestSelections}/>
            </div>
          </div>
        </Modal>
        <Modal isOpen={modalState === USER_INTERESTS_MODAL_STATE.OPT_IN}>
          <div className={s.optInModalContainer} data-testid="opt-in-modal">
            {interestsPicked && (<div className={s.optInEyebrow}>Thanks for choosing your interests!</div>)}
            <div className={s.optInLogo}>
              <NewsletterOptInOwl />
            </div>
            <div className={s.optInHeading}>
              Sign up for the Harry Potter Fan Club Newsletter for even more magic!
            </div>
            <div className={s.optInLegalText}>
              By clicking &quot;Yes, count me in&quot;, you agree to be sent Harry Potter news and
              offers from Wizarding World Digital LLC and its affiliates. You may opt-out at any
              time.
            </div>
            <div className={s.ctaContainer}>
              <Button label="Not Now" data-testid="opt-in-cancel-button" disabled={isSaving} onClick={() => {
            trackClickEvent({
                destination_url: window.location.pathname,
                label: 'Not Now',
                location: userInterestsOptInModalLocation
            });
            setModalState(USER_INTERESTS_MODAL_STATE.NONE);
        }}/>
              <Button label="Yes, Count Me In" data-testid="opt-in-accept-button" loading={isSaving} onClick={handleOptInAccept}/>
            </div>
          </div>
        </Modal>
      </div>
    </>);
};
export default UserInterests;

    async function __Next_Translate__getStaticProps__195ca0f16ae__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'src//Profile/components/UserPanel/UserInterests/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__195ca0f16ae__ as getStaticProps }
  